/*
 * Pure helpers for the Rankings (Google Search Console) views.  The backend
 * (backend/search_console*.py) does the maths; these only filter, format and
 * scale what it returns, so they are unit-tested without the API.
 */

/* Position filters.  "Close" is the band just below the top of page one that
   the opportunities list works from (backend OPP_MIN_POS..TRACK_MAX_POS). */
export const POSITION_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'top3', label: 'Top 3', test: (p) => p <= 3.5 },
  { key: 'page1', label: 'Page one', test: (p) => p <= 10.5 },
  { key: 'close', label: 'Close (8–20)', test: (p) => p >= 8 && p <= 20 },
  { key: 'page2', label: 'Page two', test: (p) => p > 10.5 && p <= 20 },
];

export const filterByPosition = (rows, key) => {
  const f = POSITION_FILTERS.find((x) => x.key === key);
  if (!f || !f.test) return rows || [];
  return (rows || []).filter((r) => r.position != null && f.test(r.position));
};

export const matchesText = (row, text, field) => {
  const t = (text || '').trim().toLowerCase();
  return !t || String(row[field] || '').toLowerCase().includes(t);
};

/* Sort descending by a numeric key; position sorts ascending (1 is best). */
export const sortRows = (rows, key) => {
  const out = [...(rows || [])];
  const asc = key === 'position';
  out.sort((a, b) => {
    const av = a[key] ?? (asc ? Infinity : -Infinity);
    const bv = b[key] ?? (asc ? Infinity : -Infinity);
    if (typeof av === 'string' || typeof bv === 'string') return String(av).localeCompare(String(bv));
    return asc ? av - bv : bv - av;
  });
  return out;
};

export const formatPos = (p) => (p == null ? '—' : Number(p).toFixed(1));

/* Position change, previous minus current: positive means we moved UP. */
export const posChange = (cur, prev) => (cur == null || prev == null ? null : Math.round((prev - cur) * 10) / 10);

export const pctChange = (cur, prev) => {
  if (!prev) return cur ? null : 0;
  return Math.round(((cur - prev) / prev) * 100);
};

export const formatUsd = (v) => {
  if (v == null) return '—';
  if (v > 0 && v < 0.01) return '<$0.01';
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/* URL -> "/path/" for display (the host is the site itself). */
export const pathOf = (url) => {
  try {
    const u = new URL(url);
    return u.pathname + (u.search || '');
  } catch (e) {
    return url || '';
  }
};

/* Page URL -> the DAM page editor slug ('' / '/' is the home page). */
export const slugOf = (url) => pathOf(url).replace(/^\/+|\/+$/g, '').split('?')[0];

/* Weekly positions for the trend line, oldest first. */
export const weeklyPositions = (weekly) => (weekly || []).map((w) => w.position).filter((p) => p != null);

/* What each topic-cluster action means (backend search_console_topics.classify). */
export const ACTIONS = {
  'double down': { badge: 'success', hint: 'Google shows us here far more than we write about it, and we rank 1–20: write more on this topic.' },
  core: { badge: 'info', hint: 'The topic with the most traction.' },
  emerging: { badge: 'warn', hint: 'Impressions ahead of content but deep (avg position past 20): Google is testing us; a first strong page could take it.' },
  push: { badge: 'info', hint: 'Queries at positions 4–20 with a closely matching page: small edits can move them up.' },
  repurpose: { badge: 'danger', hint: 'Content Google barely shows (under 3% of impressions), on pages over 90 days old: merge into a stronger page and 301, or retarget. Never plain deletion.' },
  hold: { badge: '', hint: 'Nothing to act on yet.' },
};

/* Linear scale of map points into a w x h box with padding (equal x/y units,
   so cosine distances are not stretched). */
export const mapScale = (points, w, h, pad = 12) => {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  if (!xs.length) return () => [w / 2, h / 2];
  const minX = Math.min(...xs); const maxX = Math.max(...xs);
  const minY = Math.min(...ys); const maxY = Math.max(...ys);
  const span = Math.max(maxX - minX, maxY - minY) || 1;
  const k = Math.min(w - 2 * pad, h - 2 * pad) / span;
  const ox = (w - (maxX - minX) * k) / 2;
  const oy = (h - (maxY - minY) * k) / 2;
  return (p) => [ox + (p.x - minX) * k, h - (oy + (p.y - minY) * k)];
};
